import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RBACGroup, RBACUser } from '../../../types';
import {
  useAddGroupMemberMutation,
  useGetUserGroupMembershipQuery,
} from '../../graph/services/graphApi';
import { errorMessage, isAlreadyMember, withTransientRetry } from '../../graph/transientRetry';
import { copyableIds, deriveRolesToCopy, type RegionCode } from '../copyRoles';

import ReferenceUserPicker from './ReferenceUserPicker';
import RoleCopyReview from './RoleCopyReview';

type Props = {
  users?: RBACUser[];
  // The primary (currently-selected) user — the copy target. Matches how SubmitChanges
  // receives the page's selected user.
  user?: RBACUser;
};

type Step = 'pick' | 'review' | 'running' | 'done';
type Failure = { role: RBACGroup; message: string };

// The Copy Roles flow (see issue #104): a modal on the Users page that copies a peer's
// direct Role_ memberships onto the selected user. Self-contained — it picks a reference
// user, previews the net-new roles, then writes them via addGroupMember (whose optimistic
// patch refreshes the selected user's on-page role list) with a progress indicator.
export default function CopyRoles({ users, user }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('pick');

  const [region, setRegion] = useState<RegionCode | ''>('');
  const [department, setDepartment] = useState('');
  const [query, setQuery] = useState('');

  const [referenceUser, setReferenceUser] = useState<RBACUser | undefined>();
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  // Armed on every peer pick (even re-picking the same peer); the effect below consumes it
  // to default the selection once per pick, never on background role refetches.
  const [pendingDefault, setPendingDefault] = useState(false);

  const [completed, setCompleted] = useState(0);
  // Total captured at apply time — deriveRolesToCopy shrinks live as addGroupMember's
  // optimistic patch adds each role to primaryRoles, so it can't be the progress total.
  const [total, setTotal] = useState(0);
  const [failures, setFailures] = useState<Failure[]>([]);

  const [addGroupMember] = useAddGroupMemberMutation();

  const { data: primaryRoles, isFetching: primaryRolesFetching } = useGetUserGroupMembershipQuery(
    user?.id ?? '',
    { skip: !open || !user }
  );
  const {
    data: referenceRoles,
    isFetching: referenceRolesFetching,
    isError: referenceRolesError,
    refetch: refetchReferenceRoles,
  } = useGetUserGroupMembershipQuery(referenceUser?.id ?? '', { skip: !open || !referenceUser });

  // Default the selection to every copyable role, exactly once per pick — once both the
  // peer's and the primary's roles have loaded. Consuming the pendingDefault flag (rather
  // than firing on every dep change) keeps a late/background primaryRoles update from
  // clobbering the operator's manual checkbox choices, while still re-defaulting on a new pick.
  useEffect(() => {
    if (!pendingDefault || !referenceUser || !referenceRoles || primaryRolesFetching) return;
    setSelectedIds(copyableIds(referenceRoles, primaryRoles ?? []));
    setPendingDefault(false);
  }, [pendingDefault, referenceUser, referenceRoles, primaryRoles, primaryRolesFetching]);

  const rolesToCopy = useMemo(
    () => deriveRolesToCopy(referenceRoles ?? [], primaryRoles ?? [], selectedIds),
    [referenceRoles, primaryRoles, selectedIds]
  );

  const reset = () => {
    setStep('pick');
    setRegion('');
    setDepartment('');
    setQuery('');
    setReferenceUser(undefined);
    setSelectedIds(new Set());
    setPendingDefault(false);
    setCompleted(0);
    setTotal(0);
    setFailures([]);
  };

  const openModal = () => {
    reset();
    setOpen(true);
  };

  // Dismiss via Escape, a backdrop click, or the Cancel/Close buttons — but never while the
  // writes are in flight ('running'), so a stray keypress or click can't abandon them.
  const closeModal = useCallback(() => {
    setOpen((wasOpen) => (step === 'running' ? wasOpen : false));
  }, [step]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeModal]);

  const toggleRole = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAllCopyable = () => {
    setSelectedIds(copyableIds(referenceRoles ?? [], primaryRoles ?? []));
  };

  const apply = async () => {
    if (!user || rolesToCopy.length === 0) return;
    setStep('running');
    setCompleted(0);
    // Capture the count now: the optimistic patch shrinks rolesToCopy as each write lands,
    // so total (the progress denominator) must be snapshotted before the writes start.
    setTotal(rolesToCopy.length);
    setFailures([]);

    await Promise.all(
      rolesToCopy.map(async (role) => {
        try {
          // withTransientRetry absorbs Graph throttling (429/503) and the concurrent-
          // modification / not-yet-replicated race, same as the roles- and ACLs-page
          // submit flows — these parallel writes would otherwise fail spuriously.
          await withTransientRetry(() =>
            addGroupMember({
              groupDisplayName: role.displayName,
              groupId: role.id,
              memberId: user.id,
            }).unwrap()
          );
        } catch (error) {
          // Already a member is an idempotent no-op, not a failure worth surfacing.
          if (!isAlreadyMember(error)) {
            setFailures((prev) => [
              ...prev,
              { message: errorMessage(error) ?? 'unknown error', role },
            ]);
          }
        } finally {
          setCompleted((prev) => prev + 1);
        }
      })
    );

    setStep('done');
  };

  const modalClassName = classNames('modal fixed inset-0 flex items-center justify-center', {
    'modal-open': open,
  });

  const succeeded = completed - failures.length;

  return (
    <>
      <button className="btn btn-secondary" disabled={!user} onClick={openModal}>
        Copy Roles
      </button>

      <div className={modalClassName}>
        {/* Native <button> backdrop (behind the box) so a click outside dismisses the modal
            without the jsx-a11y violations a click handler on a plain <div> would raise —
            keyboard users get the same via the Escape handler above. */}
        <button
          type="button"
          aria-label="Close"
          className="absolute inset-0 cursor-default"
          onClick={closeModal}
        />
        <div className="relative bg-base-100 rounded-lg p-5 w-[56rem] max-w-[95vw] h-[54rem] max-h-[95vh] flex flex-col">
          <div className="flex-none">
            <div className="font-bold text-xl mb-3">Copy Roles</div>
          </div>

          {/* Fixed height so every step is the same size; the body never scrolls — each
              step fills this area and its list scrolls internally. */}
          <div className="flex-1 min-h-0">
            {user && step === 'pick' && (
              <ReferenceUserPicker
                users={users ?? []}
                primaryUser={user}
                region={region}
                department={department}
                query={query}
                onRegionChange={setRegion}
                onDepartmentChange={setDepartment}
                onQueryChange={setQuery}
                onSelect={(candidate) => {
                  setReferenceUser(candidate);
                  setPendingDefault(true);
                  setStep('review');
                }}
              />
            )}

            {user && step === 'review' && referenceUser && (
              <RoleCopyReview
                primaryUser={user}
                referenceUser={referenceUser}
                referenceRoles={referenceRoles ?? []}
                primaryRoles={primaryRoles ?? []}
                isLoadingRoles={referenceRolesFetching || primaryRolesFetching}
                loadError={referenceRolesError}
                onRetryLoad={refetchReferenceRoles}
                selectedIds={selectedIds}
                rolesToCopy={rolesToCopy}
                onToggle={toggleRole}
                onSelectAll={selectAllCopyable}
                onClear={() => setSelectedIds(new Set())}
              />
            )}

            {(step === 'running' || step === 'done') && (
              <div>
                <p className="text-sm mb-2">
                  {step === 'running' ? 'Assigning roles…' : 'Done.'} {completed} / {total}
                </p>
                <progress
                  className="progress progress-primary w-full mb-3"
                  value={completed}
                  max={total || 1}
                />
                {step === 'done' && (
                  <div className="text-sm">
                    <div className="text-success mb-1">
                      {succeeded} role{succeeded === 1 ? '' : 's'} assigned to {user?.displayName}.
                    </div>
                    {failures.length > 0 && (
                      <div className="alert alert-error py-2 flex-col items-start text-xs">
                        <span>{failures.length} failed:</span>
                        {failures.map((failure) => (
                          <span key={failure.role.id}>
                            {failure.role.displayName}: {failure.message}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex-none flex justify-end mt-4 gap-2">
            {step === 'pick' && (
              <button className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
            )}
            {step === 'review' && (
              <>
                <button className="btn btn-ghost" onClick={() => setStep('pick')}>
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  disabled={rolesToCopy.length === 0}
                  onClick={apply}
                >
                  Assign roles
                </button>
              </>
            )}
            {step === 'running' && (
              <button className="btn btn-ghost" disabled>
                Assigning…
              </button>
            )}
            {step === 'done' && (
              <>
                {/* On failures (e.g. on-prem/AD-synced roles that can't be changed via Graph)
                    let the operator go back to review to deselect them or retry the rest. */}
                {failures.length > 0 && (
                  <button className="btn btn-ghost" onClick={() => setStep('review')}>
                    Back
                  </button>
                )}
                <button className="btn btn-primary" onClick={closeModal}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
